import React, { useState } from "react";

import { SnackbarData, SnackbarType } from "../../../components/Snackbar";
import ChangePasswordForm from "../../../components/forms/user/ChangePasswordForm";
import UserDetailsForm from "../../../components/forms/user/UserDetailsForm";
import ModelErrorComponent from "../../../components/modals/ModelErrorComponent";
import type {
  OverlayState,
  PaginatedUsers,
  PasswordFormValues,
  UserFormValues,
  UserRecord,
} from "../../../features/users/userTypes";
import { makePostApiCall } from "../../../shared/makeApiCall";
import { generateUniqueNumber } from "../../../shared/utility";

const emptyOverlay: OverlayState = { childComponent: null, open: false };

interface ApiCallResult {
  success?: boolean;
  data?: Record<string, any>;
  errorStatus?: number;
}

export function UsersViewModel(
  logout: () => void,
  addSnackbar: (snackbar: SnackbarData) => void,
) {
  const [loading, setLoading] = useState(false);
  const [users, setUsers] = useState<PaginatedUsers>({ data: [] });
  const [slideOver, setSlideOver] = useState<OverlayState>(emptyOverlay);
  const [emptyModal, setEmptyModal] = useState<OverlayState>(emptyOverlay);

  const getUsers = async (search: string | null, page: number) => {
    setLoading(true);
    const result = (await makePostApiCall("users", {
      page,
      search,
    })) as ApiCallResult;
    setLoading(false);

    if (result.success) {
      setUsers(result.data as PaginatedUsers);
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  const showErrorModal = (title?: string, subtitle?: string) => {
    setEmptyModal({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModal}
          subtitle={subtitle}
          title={title ?? "Unable to complete the request"}
        />
      ),
      open: true,
    });
  };

  const createUser = async (values: UserFormValues) => {
    const result = (await makePostApiCall(
      "createUser",
      values,
    )) as ApiCallResult;
    if (result.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "User added",
          SnackbarType.Success,
        ),
      );
      setSlideOver(emptyOverlay);
      setTimeout(() => void getUsers("", 1), 1000);
      return;
    }

    if (result.errorStatus === 401) logout();
    showErrorModal(result.data?.message);
  };

  const updateUser = async (user: UserRecord, values: UserFormValues) => {
    const result = (await makePostApiCall(
      `users/${user.id}`,
      values,
    )) as ApiCallResult;
    if (result.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "User updated",
          SnackbarType.Success,
        ),
      );
      setSlideOver(emptyOverlay);
      setTimeout(() => void getUsers("", 1), 1000);
      return;
    }

    if (result.errorStatus === 401) logout();
    showErrorModal(result.data?.message);
  };

  const onChangePassword = async (
    user: UserRecord,
    values: PasswordFormValues,
  ) => {
    const result = (await makePostApiCall(
      `changeUserPassword/${user.id}`,
      values,
    )) as ApiCallResult;
    if (result.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "User password updated",
          SnackbarType.Success,
        ),
      );
      setEmptyModal(emptyOverlay);
      return;
    }

    if (result.errorStatus === 401) logout();
    showErrorModal(result.data?.message);
  };

  const openChangePasswordModal = (user: UserRecord) => {
    setEmptyModal({
      childComponent: (
        <ChangePasswordForm
          user={user}
          setOpen={setEmptyModal}
          onChangePassword={(values) => void onChangePassword(user, values)}
        />
      ),
      open: true,
    });
  };

  const openAddUserSlideOver = () => {
    setSlideOver({
      childComponent: (
        <UserDetailsForm
          isEdit={false}
          setOpen={setSlideOver}
          actionCall={(values) => void createUser(values)}
        />
      ),
      open: true,
    });
  };

  const viewUserDetails = (user: UserRecord) => {
    setSlideOver({
      childComponent: (
        <UserDetailsForm
          user={user}
          isEdit={true}
          setOpen={setSlideOver}
          actionCall={(values) => void updateUser(user, values)}
        />
      ),
      open: true,
    });
  };

  const deleteUser = async (user: UserRecord) => {
    const result = (await makePostApiCall(
      `deleteUser/${user.id}`,
    )) as ApiCallResult;
    if (result.success) {
      addSnackbar(
        new SnackbarData(
          generateUniqueNumber(),
          "User deleted",
          SnackbarType.Success,
        ),
      );
      setUsers((current) => ({
        ...current,
        data: current.data.filter(({ id }) => id !== user.id),
      }));
    } else if (result.errorStatus === 401) {
      logout();
    }
  };

  return {
    openAddUserSlideOver,
    getUsers,
    loading,
    users,
    setSlideOver,
    slideOver,
    viewUserDetails,
    deleteUser,
    emptyModal,
    setEmptyModal,
    openChangePasswordModal,
  };
}
